import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { isModelStale, processCoreModelResponse } from "@/store/-utils";
import ProductDetailsApi from "@/store/product-details/-api";
import { type ProductDetailMap } from "@/store/product-details/types";
import logger, { logLevel } from "@/utils/logger";

@Module({
  name: "product-details",
  stateFactory: true,
  namespaced: true,
})
export default class ProductDetailsStore extends VuexModule {
  productDetails: ProductDetailMap = {};

  @Action({ rawError: true })
  async fetchProductDetailsByIds({ ids }: { ids: string[] }) {
    if (ids.length === 0) {
      return;
    }
    const api = new ProductDetailsApi(this.store.$axios);
    const response = await api.get({ ids });

    await processCoreModelResponse(this.store, response.data);
  }

  /**
   * Normally calling 1 mutation for every 1 model would be discouraged, however, the underlying
   * API call has a special debounce and argument aggregation decorator on it. Because of this,
   * multiple rapid calls to the API will be aggregated into a single call, resulting with only
   * the last call returning results, and the earlier ones having a rejected/cancelled promise.
   */
  @Action({ rawError: true })
  async fetchProductDetailById(id: string) {
    // Skip refreshing recent models
    if (!isModelStale(this.productDetails[id])) {
      return;
    }

    try {
      const api = new ProductDetailsApi(this.store.$axios);
      const response = await api.getOne(id);

      await processCoreModelResponse(this.store, response.data);
    } catch (error) {
      // null is returned from the debounce decorator. All other errors should be considered action failures.
      if (error === null) {
        return;
      }

      logger(logLevel.ERROR, "Error fetching product details by id", { error, productDetailId: id });
    }
  }

  @Mutation
  setProductDetails({ productDetails }: { productDetails: ProductDetailMap }) {
    // Set core models first
    Vue.set(this, "productDetails", Object.assign({}, this.productDetails, productDetails));
  }
}

export const getProductDetailsStore = (store: Store<unknown>) => {
  const productDetailsStore = getModule(ProductDetailsStore, store);

  return productDetailsStore;
};
